import { useCallback, useEffect, useRef, useState } from "react";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Download, ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { inputCls, F } from "./CrmShared";

const TARGETS = [
  ["", "— Skip this column —"], ["name", "Full Name *"], ["company", "Company / Business"], ["phone", "Phone"],
  ["whatsapp", "WhatsApp"], ["email", "Email"], ["city", "City"], ["source", "Source"], ["requirement", "Requirement / Notes"],
  ["value", "Deal Value (₹)"], ["assigned_to", "Assigned User"], ["campaign", "Campaign"], ["custom", "Custom CRM Field (label = column name)"],
];

export default function CrmImport({ refreshKey }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [mapping, setMapping] = useState({});
  const [dupMode, setDupMode] = useState("skip");
  const [pipelines, setPipelines] = useState([]);
  const [pipelineId, setPipelineId] = useState("");
  const [stage, setStage] = useState("lead");
  const [savedMaps, setSavedMaps] = useState([]);
  const [mapName, setMapName] = useState("");
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/store-admin/crm/pipelines").then((r) => setPipelines(r.data.pipelines)).catch(() => {});
    api.get("/store-admin/crm/import/mappings").then((r) => setSavedMaps(r.data.mappings)).catch(() => {});
  }, [refreshKey]);

  const parseFile = async (fl) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", fl);
      const r = await api.post("/store-admin/crm/import/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setParsed(r.data);
      const auto = {};
      r.data.columns.forEach((c) => {
        const k = c.toLowerCase().replace(/[^a-z]/g, "");
        if (["name", "fullname", "customername", "leadname"].includes(k)) auto[c] = "name";
        else if (["phone", "mobile", "phonenumber", "contact", "mobilenumber"].includes(k)) auto[c] = "phone";
        else if (k.includes("whatsapp")) auto[c] = "whatsapp";
        else if (["email", "emailaddress", "mail"].includes(k)) auto[c] = "email";
        else if (["company", "business", "businessname", "companyname"].includes(k)) auto[c] = "company";
        else if (["city", "location", "town"].includes(k)) auto[c] = "city";
        else if (["source", "leadsource"].includes(k)) auto[c] = "source";
        else if (["value", "dealvalue", "amount", "budget"].includes(k)) auto[c] = "value";
        else if (["campaign", "campaignname"].includes(k)) auto[c] = "campaign";
        else if (["notes", "requirement", "requirements", "message", "remarks"].includes(k)) auto[c] = "requirement";
        else auto[c] = "";
      });
      setMapping(auto);
      setStep(2);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const run = async () => {
    if (!Object.values(mapping).includes("name")) { toast.error("Map a column to 'Full Name' — it's required"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mapping", JSON.stringify(mapping));
      fd.append("dup_mode", dupMode);
      fd.append("pipeline_id", pipelineId);
      fd.append("default_stage", stage);
      if (mapName.trim()) fd.append("mapping_name", mapName.trim());
      const r = await api.post("/store-admin/crm/import/run", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReport(r.data);
      setStep(4);
      toast.success(`Imported ${r.data.imported} leads`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const downloadErrors = () => {
    if (!report?.errors?.length) return;
    const csv = "Row,Reason,Data\n" + report.errors.map((e) => `${e.row},"${String(e.reason).replace(/"/g, '""')}","${String(e.data || "").replace(/"/g, '""')}"`).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "import-errors.csv";
    a.click();
  };

  const applySaved = (name) => {
    const m = savedMaps.find((x) => x.name === name);
    if (m) setMapping(m.mapping);
  };

  const steps = ["Upload File", "Map Fields", "Preview & Import", "Report"];

  return (
    <div className="space-y-5" data-testid="crm-import">
      <div>
        <h2 className="font-display font-bold text-lg">Import Leads</h2>
        <p className="text-xs text-muted-foreground">Upload CSV or Excel → map columns → validate → detect duplicates → import.</p>
      </div>

      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {steps.map((s, i) => (
          <div key={s} className={`shrink-0 h-8 px-3 rounded-full text-[11px] font-extrabold flex items-center gap-1.5 border ${step === i + 1 ? "bg-emerald-700 text-white border-emerald-700" : step > i + 1 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "border-border text-muted-foreground"}`}>
            {step > i + 1 ? <CheckCircle2 className="h-3 w-3" /> : null}{i + 1}. {s}
          </div>
        ))}
      </div>

      {step === 1 && (
        <label className="block rounded-2xl border-2 border-dashed border-border p-10 text-center cursor-pointer hover:border-emerald-500 transition-colors" data-testid="import-drop">
          <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
          <p className="font-bold text-sm">{file ? file.name : "Click to choose CSV / XLSX file"}</p>
          <p className="text-xs text-muted-foreground mt-1">Max 10MB · first row must be column headers</p>
          <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden" data-testid="import-file"
            onChange={(e) => { const fl = e.target.files?.[0]; if (fl) { setFile(fl); parseFile(fl); } e.target.value = ""; }} />
          {busy && <p className="text-xs font-bold mt-2">Reading file…</p>}
        </label>
      )}

      {step === 2 && parsed && (
        <div className="space-y-4" data-testid="import-mapping">
          <div className="flex flex-wrap items-center gap-2 text-xs font-bold text-muted-foreground">
            <FileSpreadsheet className="h-4 w-4" /> {file?.name} · {parsed.total_rows} rows detected
          </div>
          {savedMaps.length > 0 && (
            <F label="Saved mappings">
              <select className={`${inputCls} w-auto`} data-testid="import-saved-maps" defaultValue="" onChange={(e) => applySaved(e.target.value)}>
                <option value="">Load saved mapping…</option>
                {savedMaps.map((m) => <option key={m.name} value={m.name}>{m.name}</option>)}
              </select>
            </F>
          )}
          <div className="grid sm:grid-cols-2 gap-2.5">
            {parsed.columns.map((c) => (
              <div key={c} className="flex items-center gap-2 rounded-xl border border-border p-2.5">
                <span className="text-xs font-bold flex-1 truncate" title={c}>{c}</span>
                <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
                <select className="h-9 rounded-lg border border-input bg-background px-2 text-xs font-bold w-40" data-testid={`map-${c}`}
                  value={mapping[c] ?? ""} onChange={(e) => setMapping({ ...mapping, [c]: e.target.value })}>
                  {TARGETS.map(([v, l]) => <option key={v + l} value={v}>{l}</option>)}
                </select>
              </div>
            ))}
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <F label="Duplicates">
              <select className={inputCls} data-testid="import-dup-mode" value={dupMode} onChange={(e) => setDupMode(e.target.value)}>
                <option value="skip">Skip duplicate</option><option value="update">Update existing lead</option><option value="create">Create new lead</option>
              </select>
            </F>
            <F label="Pipeline">
              <select className={inputCls} data-testid="import-pipeline" value={pipelineId} onChange={(e) => setPipelineId(e.target.value)}>
                <option value="">Sales Pipeline (default)</option>
                {pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </F>
            <F label="Default stage">
              <select className={inputCls} data-testid="import-stage" value={stage} onChange={(e) => setStage(e.target.value)}>
                {(pipelines.find((p) => p.id === pipelineId)?.stages || pipelines[0]?.stages || [{ key: "lead", name: "Lead" }]).map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
              </select>
            </F>
          </div>
          <F label="Save this mapping as (optional)"><input className={inputCls} data-testid="import-map-name" value={mapName} onChange={(e) => setMapName(e.target.value)} placeholder="e.g. Facebook Export" /></F>
          <div className="flex gap-2">
            <button onClick={() => setStep(1)} className="h-11 px-5 rounded-xl border border-border font-bold text-sm flex items-center gap-1.5"><ArrowLeft className="h-4 w-4" /> Back</button>
            <button onClick={() => setStep(3)} className="flex-1 h-11 rounded-xl bg-foreground text-background font-bold text-sm" data-testid="import-preview-btn">Preview & Validate</button>
          </div>
        </div>
      )}

      {step === 3 && parsed && (
        <div className="space-y-4" data-testid="import-preview">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[["Total Rows", parsed.total_rows], ["Mapped Columns", Object.values(mapping).filter(Boolean).length],
              ["Duplicate Rule", dupMode], ["Ready", Object.values(mapping).includes("name") ? "Yes" : "No"]].map(([l, v]) => (
              <div key={l} className="rounded-xl border border-border bg-background p-3 text-center">
                <p className="font-extrabold text-lg capitalize">{v}</p><p className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground">{l}</p>
              </div>
            ))}
          </div>
          <div className="rounded-2xl border border-border overflow-x-auto">
            <table className="w-full text-xs min-w-[480px]">
              <thead><tr className="bg-muted/50">{parsed.columns.filter((c) => mapping[c]).map((c) => <th key={c} className="text-left px-3 py-2 font-extrabold">{c} → {mapping[c]}</th>)}</tr></thead>
              <tbody>
                {parsed.sample.map((row, i) => (
                  <tr key={i} className="border-t border-border">{parsed.columns.filter((c) => mapping[c]).map((c) => <td key={c} className="px-3 py-2 truncate max-w-[160px]">{row[c] || "—"}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-muted-foreground">Preview shows first 5 mapped rows. Invalid rows are never silently discarded — you get a full error report.</p>
          <div className="flex gap-2">
            <button onClick={() => setStep(2)} className="h-11 px-5 rounded-xl border border-border font-bold text-sm flex items-center gap-1.5"><ArrowLeft className="h-4 w-4" /> Back</button>
            <button onClick={run} disabled={busy} data-testid="import-run"
              className="flex-1 h-11 rounded-xl font-bold text-sm text-white disabled:opacity-60"
              style={{ backgroundImage: "linear-gradient(135deg, #059669, #0d9488)" }}>
              {busy ? "Importing…" : `Import ${parsed.total_rows} Leads`}
            </button>
          </div>
        </div>
      )}

      {step === 4 && report && (
        <div className="space-y-4" data-testid="import-report">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {[["Imported", report.imported, "text-emerald-600"], ["Updated", report.updated, "text-blue-600"],
              ["Duplicates Skipped", report.skipped_duplicates, "text-amber-600"], ["Invalid", report.invalid, "text-red-500"],
              ["Failed", report.failed, "text-red-500"]].map(([l, v, c]) => (
              <div key={l} className="rounded-xl border border-border bg-background p-3 text-center">
                <p className={`font-extrabold text-xl ${c}`}>{v}</p><p className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground">{l}</p>
              </div>
            ))}
          </div>
          {report.errors.length > 0 && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-bold flex items-center gap-1.5"><AlertTriangle className="h-4 w-4 text-amber-600" /> {report.errors.length} row(s) need attention</p>
                <button onClick={downloadErrors} data-testid="import-errors-dl" className="h-9 px-4 rounded-xl border border-border bg-background text-xs font-extrabold flex items-center gap-1.5"><Download className="h-3.5 w-3.5" /> Error Report</button>
              </div>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {report.errors.slice(0, 50).map((e, i) => (
                  <p key={i} className="text-xs"><span className="font-bold">Row {e.row}:</span> {e.reason}{e.data ? ` (${e.data})` : ""}</p>
                ))}
              </div>
            </div>
          )}
          <button onClick={() => { setStep(1); setFile(null); setParsed(null); setReport(null); }} data-testid="import-again"
            className="h-11 px-6 rounded-xl bg-foreground text-background font-bold text-sm">Import Another File</button>
        </div>
      )}
    </div>
  );
}
